import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, ChevronDown, ChevronUp, Percent, UserCircle2, X, Minus, Plus, Trash2 } from "lucide-react";
import { readCart, removeLine, setLineQty, cartTotals, subCart } from "../../core/cart";

export default function CartSection({ section }) {
  const navigate = useNavigate();
  const [lines, setLines] = useState(readCart);
  const [openSummary, setOpenSummary] = useState(false);
  const [phone, setPhone] = useState("");
  const [optIn, setOptIn] = useState(false);
  const emptyMessage = section.emptyMessage || "Your cart is empty.";
  const checkoutPath = section.checkoutPath || "/checkout";
  const offerBanner = section.offerBanner || "Buy 2 Get 1 Absolutely FREE";

  useEffect(() => subCart(() => setLines(readCart())), []);

  useEffect(() => {
    if (!lines.length) setOpenSummary(false);
  }, [lines.length]);

  const { subtotal } = cartTotals(lines);
  const totals = useMemo(() => {
    const mrpTotal = lines.reduce((acc, line) => acc + (Number(line.mrp) || Number(line.unitPrice) || 0) * (Number(line.qty) || 1), 0);
    const save = Math.max(0, mrpTotal - subtotal);
    const totalQty = lines.reduce((acc, line) => acc + (Number(line.qty) || 1), 0);
    return { mrpTotal, save, totalQty };
  }, [lines, subtotal]);

  if (!lines.length) {
    return (
      <div className="lfx-card lfx-cart-card">
        <div className="lfx-card-body">
          <div className="lfx-empty-state">{emptyMessage}</div>
          <button type="button" className="lfx-btn secondary" style={{ marginTop: 12 }} onClick={() => navigate("/products")}>
            Continue shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="lfx-cart-checkout">
      <div className="lfx-cart-offer-strip">{offerBanner}</div>

      <div className="lfx-cart-panel">
        <button type="button" className="lfx-cart-summary-head" onClick={() => setOpenSummary((v) => !v)}>
          <div className="lfx-cart-summary-head-left">
            <ShoppingCart size={20} />
            <div>
              <div className="lfx-cart-summary-title">Order Summary</div>
              {totals.save > 0 ? (
                <div className="lfx-cart-saving-pill">₹{totals.save.toLocaleString("en-IN")} saved so far</div>
              ) : null}
            </div>
          </div>
          <div className="lfx-cart-summary-head-right">
            <span>
              {totals.totalQty} item{totals.totalQty !== 1 ? "s" : ""}
            </span>
            <div>
              {totals.mrpTotal > subtotal ? <em>₹{totals.mrpTotal.toLocaleString("en-IN")}</em> : null}
              <strong>₹{subtotal.toLocaleString("en-IN")}</strong>
            </div>
            {openSummary ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </div>
        </button>

        <div className="lfx-cart-input-row">
          <Percent size={18} />
          <input type="text" placeholder="Enter coupon code" />
        </div>

        <div className="lfx-cart-login-card">
          <div className="lfx-cart-login-title">
            <UserCircle2 size={20} />
            <span>Login to continue</span>
          </div>
          <div className="lfx-cart-phone-wrap">
            <span>+91</span>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Enter Mobile Number"
            />
          </div>
        </div>

        <label className="lfx-cart-optin">
          <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} />
          <span>Send me order updates & offers - (no spam)</span>
        </label>

        <button
          type="button"
          className={`lfx-cart-next-btn ${phone.replace(/\D/g, "").length >= 10 ? "lfx-cart-next-btn--ready" : ""}`}
          onClick={() => {
            try {
              sessionStorage.setItem("lfx_checkout_phone", phone.replace(/\D/g, ""));
            } catch (e) {
              /* ignore */
            }
            navigate(checkoutPath);
          }}
        >
          Next
        </button>

        <p className="lfx-cart-tnc">
          By proceeding, I agree to Privacy Policy and T&C
        </p>
      </div>

      {openSummary ? (
        <div className="lfx-cart-drawer-backdrop" onClick={() => setOpenSummary(false)}>
          <div className="lfx-cart-drawer" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="lfx-cart-drawer-close" onClick={() => setOpenSummary(false)}>
              <X size={18} />
            </button>
            <h3>Order Summary</h3>
            <div className="lfx-cart-lines">
              {lines.map((line) => (
                <div key={line.key} className="lfx-cart-drawer-line">
                  <div className="lfx-cart-drawer-line-top">
                    <div className="lfx-cart-thumb-wrap">
                      {line.image ? (
                        <img src={line.image} alt="" className="lfx-cart-thumb" loading="lazy" />
                      ) : (
                        <div className="lfx-cart-thumb lfx-cart-thumb--empty" />
                      )}
                    </div>
                    <div className="lfx-cart-drawer-line-info">
                      <div className="lfx-cart-line-title">{line.name || "Product"}</div>
                      <div className="lfx-cart-line-meta">
                        <span>Qty: {Number(line.qty) || 1}</span>
                        {line.color ? <span>{line.color}</span> : null}
                      </div>
                      <div className="lfx-cart-line-price lfx-cart-line-price--stack">
                        {line.mrp && Number(line.mrp) > Number(line.unitPrice) ? (
                          <span className="lfx-cart-line-mrp">
                            ₹{(Number(line.mrp) * (Number(line.qty) || 1)).toLocaleString("en-IN")}
                          </span>
                        ) : null}
                        <strong>₹{(Number(line.unitPrice || 0) * (Number(line.qty) || 1)).toLocaleString("en-IN")}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="lfx-cart-drawer-line-tools">
                    <div className="lfx-cart-qty lfx-cart-qty--compact">
                      <button
                        type="button"
                        className="lfx-cart-icon-round"
                        aria-label="Decrease quantity"
                        onClick={() => {
                          setLineQty(line.key, (Number(line.qty) || 1) - 1);
                          setLines(readCart());
                        }}
                      >
                        <Minus size={14} />
                      </button>
                      <span className="lfx-cart-qty-val">{Number(line.qty) || 1}</span>
                      <button
                        type="button"
                        className="lfx-cart-icon-round"
                        aria-label="Increase quantity"
                        onClick={() => {
                          setLineQty(line.key, (Number(line.qty) || 1) + 1);
                          setLines(readCart());
                        }}
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                    <button
                      type="button"
                      className="lfx-cart-icon-round lfx-cart-icon-round--danger"
                      title="Remove"
                      onClick={() => {
                        removeLine(line.key);
                        setLines(readCart());
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="lfx-cart-breakup">
              <div><span>MRP Total</span><strong>₹{totals.mrpTotal.toLocaleString("en-IN")}</strong></div>
              {totals.save > 0 ? (
                <div><span>Discount on MRP</span><strong>₹{totals.save.toLocaleString("en-IN")}</strong></div>
              ) : null}
              <div><span>Subtotal</span><strong>₹{subtotal.toLocaleString("en-IN")}</strong></div>
              <div><span>Shipping</span><strong>To be calculated</strong></div>
              <div><span>Tax</span><strong>To be calculated</strong></div>
            </div>
            <div className="lfx-cart-pay-row">
              <span>To Pay</span>
              <strong>₹{subtotal.toLocaleString("en-IN")}</strong>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
